import type {
	ProjectDTO,
	TicketDTO,
	TicketStatus,
	TopicDTO,
} from "@zplanem/responses"
import { webPaths } from "@zplanem/web-paths"
import { useState } from "react"
import { Button } from "@/components/ui/button"
import { ApiError } from "../../../lib/api-client"
import { usePropertyMemberships } from "../../hooks/use-property-memberships"
import { usePropertyTickets, useSetTicketStatus } from "../../hooks/use-tickets"
import { getTranslations } from "../../i18n"
import { useLocale } from "../../locale-context"
import { useUser } from "../../user-context"
import {
	TicketCard,
	type TicketReturnTo,
	TicketSkeletonCards,
} from "./ticket-card"
import {
	hasTicketBoardFilters,
	type TicketBoardFilters,
	topicLabelForTicket,
} from "./ticket-filter-model"
import {
	getAllowedTicketStatusTargets,
	isTicketManager,
} from "./ticket-form-model"
import { TicketStatusMenu } from "./ticket-status-menu"
import { flattenTicketPages, groupTicketsByStatus } from "./ticket-view-model"

/**
 * The List view of the Tasks tab (ZPL-264) — a vertical, grouped-by-status
 * alternative to the Kanban board. Unlike the board (one infinite query PER
 * status column), this runs a SINGLE flat all-statuses query (status omitted) and
 * buckets the results into collapsible sections, in the board's status order.
 * No drag-drop; status changes go through the per-row status menu.
 */
export function TicketList({
	propertyId,
	filters,
	topics,
	projects = [],
	showProjectLabels = true,
	returnTo,
	lockedProjectId,
}: {
	propertyId: string
	filters: TicketBoardFilters
	topics: TopicDTO[]
	projects?: ProjectDTO[]
	showProjectLabels?: boolean
	returnTo?: TicketReturnTo
	lockedProjectId?: string
}) {
	const { locale } = useLocale()
	const t = getTranslations(locale)
	const { profile } = useUser()
	const memberships = usePropertyMemberships(propertyId)
	const canManageTickets = isTicketManager(memberships.data, profile?.id)
	// Single flat query across all statuses (the board uses one query per status).
	const query = usePropertyTickets(propertyId, filters)
	const tickets = flattenTicketPages(query.data?.pages ?? [])
	const hasFilters = hasTicketBoardFilters(filters, lockedProjectId)
	const [collapsed, setCollapsed] = useState<ReadonlySet<TicketStatus>>(
		() => new Set<TicketStatus>(),
	)

	function toggleCollapsed(status: TicketStatus) {
		setCollapsed((current) => {
			const next = new Set<TicketStatus>(current)
			if (next.has(status)) next.delete(status)
			else next.add(status)
			return next
		})
	}

	if (query.isLoading && tickets.length === 0) {
		return (
			<section aria-label={t.tickets.listAriaLabel}>
				<TicketSkeletonCards />
			</section>
		)
	}

	if (query.error && tickets.length === 0) {
		return (
			<section aria-label={t.tickets.listAriaLabel}>
				<TicketListError t={t.tickets} onRetry={() => query.refetch()} />
			</section>
		)
	}

	if (tickets.length === 0) {
		return (
			<section
				aria-label={t.tickets.listAriaLabel}
				className="rounded-lg border border-dashed border-border bg-background/60 px-3 py-10 text-center text-sm text-muted-foreground"
			>
				<div className="font-medium text-foreground">
					{hasFilters ? t.tickets.emptyFilteredTitle : t.tickets.emptyTitle}
				</div>
				<div className="mt-1 text-xs">
					{hasFilters
						? t.tickets.emptyFilteredDescription
						: t.tickets.emptyDescription}
				</div>
			</section>
		)
	}

	const groups = groupTicketsByStatus(tickets)

	return (
		<section aria-label={t.tickets.listAriaLabel} className="space-y-6">
			{groups.map((group) => {
				const isCollapsed = collapsed.has(group.status)
				// More pages may hold additional tickets of this status, so the
				// count is a lower bound until the list is fully loaded.
				const countText = query.hasNextPage
					? `${group.items.length}+`
					: String(group.items.length)
				return (
					<div key={group.status}>
						<button
							type="button"
							onClick={() => toggleCollapsed(group.status)}
							aria-expanded={!isCollapsed}
							className="mb-3 flex w-full items-center gap-2 rounded-lg px-1 py-1 text-left transition-colors hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
						>
							<ChevronIcon
								className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform ${
									isCollapsed ? "-rotate-90" : ""
								}`}
							/>
							<span className="text-sm font-semibold text-foreground">
								{t.tickets.status[group.status]}
							</span>
							<span className="rounded-sm bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
								<span className="sr-only">
									{t.tickets.statusCountLabel
										.replace("{count}", countText)
										.replace("{status}", t.tickets.status[group.status])}
								</span>
								<span aria-hidden="true">{countText}</span>
							</span>
						</button>
						{isCollapsed ? null : (
							<div className="space-y-3">
								{group.items.map((ticket) => (
									<TicketListRow
										key={ticket.id}
										ticket={ticket}
										topicLabel={topicLabelForTicket(ticket, topics)}
										projectLabel={
											showProjectLabels
												? projectLabelForTicket(ticket, projects)
												: null
										}
										projectHref={
											showProjectLabels && ticket.projectId
												? webPaths.project(ticket.propertyId, ticket.projectId)
												: null
										}
										returnTo={returnTo}
										viewerId={profile?.id}
										canManageTickets={canManageTickets}
									/>
								))}
							</div>
						)}
					</div>
				)
			})}

			{query.error && tickets.length > 0 ? (
				<TicketListError t={t.tickets} onRetry={() => query.refetch()} />
			) : null}

			{query.hasNextPage ? (
				<Button
					type="button"
					variant="outline"
					size="sm"
					className="w-full"
					disabled={query.isFetchingNextPage}
					onClick={() => {
						void query.fetchNextPage()
					}}
				>
					{query.isFetchingNextPage
						? t.tickets.loadingMore
						: t.tickets.loadMore}
				</Button>
			) : null}
		</section>
	)
}

function TicketListRow({
	ticket,
	topicLabel,
	projectLabel,
	projectHref,
	returnTo,
	viewerId,
	canManageTickets,
}: {
	ticket: TicketDTO
	topicLabel?: string | null
	projectLabel?: string | null
	projectHref?: string | null
	returnTo?: TicketReturnTo
	viewerId: string | undefined
	canManageTickets: boolean
}) {
	const { locale } = useLocale()
	const t = getTranslations(locale)
	const setStatus = useSetTicketStatus(ticket.propertyId, ticket.id)
	const [error, setError] = useState<string | null>(null)
	const canSetStatus =
		canManageTickets || (viewerId != null && ticket.assignee?.id === viewerId)
	const allowedStatuses = getAllowedTicketStatusTargets(
		ticket.status,
		canManageTickets,
	)

	return (
		<div>
			<TicketCard
				ticket={ticket}
				t={t.tickets}
				locale={locale}
				topicLabel={topicLabel}
				projectLabel={projectLabel}
				projectHref={projectHref}
				returnTo={returnTo}
				statusControl={
					canSetStatus && allowedStatuses.length > 1 ? (
						<TicketStatusMenu
							status={ticket.status}
							statuses={allowedStatuses}
							pending={setStatus.isPending}
							onSelect={async (status) => {
								setError(null)
								try {
									await setStatus.mutateAsync({ status })
								} catch (err) {
									setError(
										err instanceof ApiError
											? err.message
											: t.tickets.statusChangeError,
									)
								}
							}}
						/>
					) : undefined
				}
			/>
			{error ? (
				<div
					role="alert"
					className="mt-2 rounded-lg border border-destructive bg-destructive px-3 py-2 text-xs text-destructive-foreground"
				>
					{error}
				</div>
			) : null}
		</div>
	)
}

function TicketListError({
	t,
	onRetry,
}: {
	t: ReturnType<typeof getTranslations>["tickets"]
	onRetry: () => void
}) {
	return (
		<div
			role="alert"
			className="rounded-lg border border-destructive bg-destructive px-3 py-3 text-sm text-destructive-foreground"
		>
			<div className="font-medium">{t.errorTitle}</div>
			<Button
				type="button"
				variant="outline"
				size="sm"
				onClick={onRetry}
				className="mt-2"
			>
				{t.errorRetry}
			</Button>
		</div>
	)
}

function projectLabelForTicket(
	ticket: Pick<TicketDTO, "projectId">,
	projects: readonly ProjectDTO[],
): string | null {
	if (!ticket.projectId) return null
	return (
		projects.find((project) => project.id === ticket.projectId)?.name ?? null
	)
}

function ChevronIcon({ className }: { className?: string }) {
	return (
		<svg
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
			className={className}
			aria-hidden="true"
		>
			<path d="M6 9l6 6 6-6" />
		</svg>
	)
}
